import { CheckCircle, Copy, Trash2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaWhatsapp } from 'react-icons/fa';
import { auth } from '../../firebase/config';
import { getProdutosByUser } from '../../services/firestoreService';
import { getFuncionarios } from '../../services/funcionarioService';
import { getClientesByUser } from '../../services/userService';
import { Cliente, Funcionario, Produto, ProdutoReserva, ReservaFormData, ReservaFormProps } from '../../types';
import { formatPrice } from '../../utils/priceUtils';
import { copyWhatsAppMessage, generateWhatsAppMessage, openWhatsApp } from '../../utils/whatsappUtils';
import InputSearch from '../Common/InputSearch';
import FormPagamentos from '../FormPagamentos';
import { FormPagamentosData } from '../FormPagamentos/types';
import ReservaConfirmModal from './ReservaConfirmModal';
import ConfirmModal from '../Common/ConfirmModal';
import { useConfirmModal } from '../../hooks/useConfirmModal';
import { toast } from 'react-toastify';

const ReservaForm: React.FC<ReservaFormProps> = ({ show, onHide, onSave, reserva = null, onSuccess, initialData = null, customHeaderContent }) => {
  const { modalState: cancelModalState, showConfirm: showCancelConfirm, hideConfirm: hideCancelConfirm, handleConfirm: handleCancelConfirm } = useConfirmModal();
  const [formData, setFormData] = useState<ReservaFormData>({
    clienteId: '',
    produtos: [],
    dataRetirada: '',
    dataDevolucao: '',
    status: 'reservado',
    observacoes: ''
  });
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [reservaCriada, setReservaCriada] = useState<{ id: string; clienteNome: string; clienteTelefone: string } | null>(null);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [pagamentosData, setPagamentosData] = useState<FormPagamentosData | undefined>(undefined);

  const [copied, setCopied] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);

  useEffect(() => {
    if (show) {
      loadData();
    }
  }, [show]);


  useEffect(() => {
    if (selectedCliente) {
      setFormData(prev => ({ ...prev, clienteId: selectedCliente.id || '' }));
    }
  }, [selectedCliente]);

  useEffect(() => {
    if (reserva) {
      setFormData({
        clienteId: reserva.clienteId || '',
        produtos: reserva.produtos || [],
        dataRetirada: reserva.dataRetirada || '',
        dataDevolucao: reserva.dataDevolucao || '',
        status: reserva.status || 'reservado',
        observacoes: reserva.observacoes || '',
        funcionarioId: reserva.funcionarioId || '',
        funcionarioNome: reserva.funcionarioNome || ''
      });

      // Definir cliente selecionado se existir
      if (reserva.clienteId && clientes.length > 0) {
        const cliente = clientes.find(c => c.id === reserva.clienteId);
        if (cliente) {
          setSelectedCliente(cliente);
        }
      }
    } else if (initialData) {
      // Usar dados iniciais (vindo de atendimento)
      setFormData(initialData);
      
      // Definir cliente selecionado se existir
      if (initialData.clienteId && clientes.length > 0) {
        const cliente = clientes.find(c => c.id === initialData.clienteId);
        if (cliente) {
          setSelectedCliente(cliente);
        }
      }
    } else {
      setFormData({
        clienteId: '',
        produtos: [],
        dataRetirada: '',
        dataDevolucao: '',
        status: 'reservado',
        observacoes: '',
        funcionarioId: '',
        funcionarioNome: ''
      });
      setSelectedCliente(null);
    }
  }, [reserva, initialData, clientes]);

  const loadData = async () => {
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      const [clientesResult, produtosResult, funcionariosResult] = await Promise.all([
        getClientesByUser(),
        getProdutosByUser(uid),
        getFuncionarios(uid)
      ]);

      if (clientesResult.success && clientesResult.data) {
        setClientes(clientesResult.data);
      } else {
        setError('Erro ao carregar clientes: ' + (clientesResult.error || 'Erro desconhecido'));
      }

      if (produtosResult.success && produtosResult.data) {
        setProdutos(produtosResult.data);
      } else {
        setError('Erro ao carregar produtos: ' + (produtosResult.error || 'Erro desconhecido'));
      }

      if (funcionariosResult.success && funcionariosResult.data) {
        setFuncionarios(funcionariosResult.data.filter(f => f.ativo));
      }
    } catch (error) {
      setError('Erro ao carregar dados');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Campos que devem ser convertidos para maiúscula
    const textFields = ['observacoes'];

    setFormData(prev => ({
      ...prev,
      [name]: textFields.includes(name) ? value.toUpperCase() : value
    }));
  };

  const handleDateChange = (field: 'dataRetirada' | 'dataDevolucao', date: Date | null) => {
    if (date) {
      setFormData(prev => ({ ...prev, [field]: date.toISOString() }));
    }
  };

  const addProduto = (produtoId: string) => {
    const produto = produtos.find(p => p.id === produtoId);
    if (!produto) return;

    // Verificar se já existe na reserva (mesmo produto, tamanho e cor)
    const jaExiste = formData.produtos.some(p =>
      p.produtoId === produtoId &&
      p.produtoTamanho === produto.tamanho &&
      p.produtoCor === produto.cor
    );

    if (jaExiste) {
      // Se já existe, apenas aumentar a quantidade
      setFormData(prev => ({
        ...prev,
        produtos: prev.produtos.map(p =>
          p.produtoId === produtoId &&
            p.produtoTamanho === produto.tamanho &&
            p.produtoCor === produto.cor
            ? {
              ...p,
              quantidade: p.quantidade + 1,
              precoTotal: (p.precoUnitario || 0) * (p.quantidade + 1)
            }
            : p
        )
      }));
      return;
    }

    const produtoReserva: ProdutoReserva = {
      produtoId: produto.id!,
      produtoTitulo: produto.titulo,
      produtoTamanho: produto.tamanho,
      produtoCor: produto.cor,
      produtoCodigoProduto: produto.codigoProduto, // Incluir código do produto
      quantidade: 1,
      precoUnitario: produto.preco || 0,
      precoTotal: produto.preco || 0,
      produtoObservacoes: '' // Iniciar com observações vazias
    };

    setFormData(prev => ({
      ...prev,
      produtos: [...prev.produtos, produtoReserva]
    }));
  };

  const removeProduto = (produtoId: string, tamanho: string, cor: string) => {
    setFormData(prev => ({
      ...prev,
      produtos: prev.produtos.filter(p =>
        !(p.produtoId === produtoId && p.produtoTamanho === tamanho && p.produtoCor === cor)
      )
    }));
  };

  const updateProdutoQuantidade = (produtoId: string, tamanho: string, cor: string, quantidade: number) => {
    const produto = produtos.find(p => p.id === produtoId);
    if (!produto) return;

    const quantidadeMaxima = produto.quantidade;
    const quantidadeFinal = Math.max(1, Math.min(quantidade, quantidadeMaxima));

    setFormData(prev => ({
      ...prev,
      produtos: prev.produtos.map(p =>
        p.produtoId === produtoId && p.produtoTamanho === tamanho && p.produtoCor === cor
          ? {
            ...p,
            quantidade: quantidadeFinal,
            precoTotal: (p.precoUnitario || 0) * quantidadeFinal
          }
          : p
      )
    }));
  };

  const updateProdutoObservacoes = (produtoId: string, tamanho: string, cor: string, observacoes: string) => {
    setFormData(prev => ({
      ...prev,
      produtos: prev.produtos.map(p =>
        p.produtoId === produtoId && p.produtoTamanho === tamanho && p.produtoCor === cor
          ? { ...p, produtoObservacoes: observacoes.toUpperCase() }
          : p
      )
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.produtos.length === 0) {
      setError('Adicione pelo menos um produto à reserva');
      setLoading(false);
      return;
    }

    if (!formData.dataRetirada) {
      toast.error('Selecione a data de retirada');
      setLoading(false);
      return;
    }

    // Em vez de salvar diretamente, abrir modal de confirmação
    setShowConfirm(true);
    setLoading(false);
  };

  const confirmSave = async () => {
    try {
      setLoading(true);
      // Incluir dados de pagamento no formData antes de salvar
      const formDataWithPayments = {
        ...formData,
        pagamentosData: pagamentosData,
        valorTotal: formData.produtos.reduce((sum, p) => sum + (p.precoTotal || 0), 0)
      };

      const result = await onSave(formDataWithPayments);

      // Se for uma nova reserva (não edição), capturar dados para WhatsApp
      if (!reserva) {
        const cliente = selectedCliente;
        if (cliente && result && typeof result === 'object' && 'id' in result) {
          const reservaData = {
            id: result.id as string,
            clienteNome: cliente.nome,
            clienteTelefone: cliente.telefone
          };

          setReservaCriada(reservaData);
          await new Promise(resolve => setTimeout(resolve, 200));
        } else {
          onHide();
        }
      } else {
        onHide();
      }
    } catch (error: any) {
      setError(error.message || 'Erro ao salvar reserva');
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  const handleWhatsApp = () => {
    if (reservaCriada) {
      openWhatsApp(reservaCriada.clienteTelefone, reservaCriada.id, reservaCriada.clienteNome);
    }
  };

  const handleCopyMessage = async () => {
    if (reservaCriada) {
      const success = await copyWhatsAppMessage(reservaCriada.id, reservaCriada.clienteNome);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleClose = () => {
    setReservaCriada(null);
    setCopied(false);
    onHide();
    if (onSuccess) {
      onSuccess();
    }
  };

  const handleCancel = () => {
    const temDados = formData.clienteId || formData.produtos.length > 0 || formData.observacoes;
    if (!temDados) {
      onHide();
      return;
    }
    showCancelConfirm(
      'Cancelar reserva',
      'Tem certeza que deseja cancelar? Os dados preenchidos serão perdidos.',
      () => {
        hideCancelConfirm();
        onHide();
      },
      'danger',
      'Sim, cancelar',
      'Continuar editando'
    );
  };

  // Se a reserva foi criada, mostrar tela de sucesso
  if (reservaCriada) {
    return (
      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        centered
        dialogClassName="modal-dialog-centered modal-xl"
        contentClassName="modal-content-centered"
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <CheckCircle size={24} className="text-success" />
            Reserva Criada com Sucesso!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="mb-4">
            <CheckCircle size={64} className="text-success mb-3" />
            <h4 className="text-success mb-2">Reserva Confirmada!</h4>
            <p className="text-muted">
              A reserva para <strong>{reservaCriada.clienteNome}</strong> foi criada com sucesso.
            </p>
          </div>

          <div className="mb-4">
            <h6 className="mb-3">Enviar confirmação via WhatsApp:</h6>
            <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
              <Button
                variant="success"
                onClick={handleWhatsApp}
                className="d-flex align-items-center gap-2"
                size="lg"
              >
                <FaWhatsapp size={25} />
                Enviar pelo WhatsApp
              </Button>
              <Button
                variant="outline-primary"
                onClick={handleCopyMessage}
                className="d-flex align-items-center gap-2"
                size="lg"
              >
                {copied ? <CheckCircle size={20} /> : <Copy size={20} />}
                {copied ? 'Copiado!' : 'Copiar Mensagem'}
              </Button>
            </div>
          </div>

          <div className="alert alert-info text-start">
            <h6 className="alert-heading">Mensagem que será enviada:</h6>
            <p className="mb-0 small">
              {generateWhatsAppMessage(reservaCriada.id, reservaCriada.clienteNome)}
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      onHide={handleCancel}
      size="xl"
      centered
      dialogClassName="modal-dialog-centered modal-xl"
      contentClassName="modal-content-centered"
    >
      <Modal.Header closeButton>
        <Modal.Title>{reserva ? 'Editar Reserva' : 'Nova Reserva'}</Modal.Title>
      </Modal.Header>
      {customHeaderContent && (
        <div style={{ 
          padding: '15px 1rem', 
          borderBottom: '1px solid #dee2e6',
          background: '#f8f9fa'
        }}>
          {customHeaderContent}
        </div>
      )}
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Row>
            <Col md={6}>
              <InputSearch
                placeholder='Buscar cliente por nome, telefone ou CPF...'
                name='cliente'
                data={clientes}
                value={selectedCliente}
                onChange={setSelectedCliente}
                onClear={() => setSelectedCliente(null)}
                size={12}
                showClearButton={true}
              />
            </Col>
            <Col md={6}>
              <InputSearch
                placeholder='Buscar produto por nome, tamanho ou cor...'
                name='produto'
                data={produtos}
                value={null}
                onChange={(produto: Produto) => addProduto(produto.id || '')}
                size={12}
              />
            </Col>
          </Row>
          
          {/* Lista de Produtos Selecionados */}
          {formData.produtos.length > 0 && (
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Produtos Selecionados</Form.Label>
                  <div className="border rounded p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {formData.produtos.map((produto, index) => (
                      <div key={`${produto.produtoId}-${produto.produtoTamanho}-${produto.produtoCor}-${index}`} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2">
                            {produto.produtoCodigoProduto && (
                              <span className="fw-bold text-primary" style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                                [{produto.produtoCodigoProduto}]
                              </span>
                            )}
                            <div className="fw-bold">{produto.produtoTitulo}</div>
                          </div>
                          <small className="text-muted">
                            {produto.produtoTamanho} - {produto.produtoCor}
                          </small>
                          {/* <div className="text-info small">
                            Disponível: {produtos.find(p => p.id === produto.produtoId)?.quantidade || 0}
                          </div> */}
                          {produto.precoUnitario && produto.precoUnitario > 0 && (
                            <div className="text-success">
                              {formatPrice(produto.precoUnitario)} cada
                            </div>
                          )}
                          <div className="d-flex flex-column gap-2 mt-2">
                            <Form.Control
                              type="text"
                              placeholder="Observações específicas deste produto..."
                              value={produto.produtoObservacoes || ''}
                              onChange={(e) => updateProdutoObservacoes(
                                produto.produtoId,
                                produto.produtoTamanho,
                                produto.produtoCor,
                                e.target.value
                              )}
                            />
                          </div>
                        </div>

                        <div className="d-flex gap-2 justify-content-end" style={{ minWidth: '300px' }}>
                          {/* Campo de observações do produto */}
                          {/* Controles de quantidade e remover */}
                          <div className="d-flex align-items-center gap-2 justify-content-end">
                            <Form.Control
                              type="number"
                              min="1"
                              max={produtos.find(p => p.id === produto.produtoId)?.quantidade || 1}
                              value={produto.quantidade}
                              onChange={(e) => updateProdutoQuantidade(produto.produtoId, produto.produtoTamanho, produto.produtoCor, parseInt(e.target.value) || 1)}
                              style={{ width: '80px', height: '40px' }}
                            />
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => removeProduto(produto.produtoId, produto.produtoTamanho, produto.produtoCor)}
                            >
                              <Trash2 size={20} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {formData.produtos.some(p => p.precoUnitario && p.precoUnitario > 0) && (
                      <div className="mt-2 pt-2 border-top">
                        <strong>
                          Total: {formatPrice(formData.produtos.reduce((sum, p) => sum + (p.precoTotal || 0), 0))}
                        </strong>
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>
          )}
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Atendente *</Form.Label>
                {initialData?.funcionarioId ? (
                  <Form.Control
                    required
                    type="text"
                    value={formData.funcionarioNome || ''}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                  />
                ) : (
                  <Form.Select
                    value={formData.funcionarioId || ''}
                    required
                    onChange={(e) => {
                      const selected = funcionarios.find(f => f.id === e.target.value);
                      setFormData(prev => ({
                        ...prev,
                        funcionarioId: selected?.id || '',
                        funcionarioNome: selected?.nome || ''
                      }));
                    }}
                  >
                    <option value="">Selecione o atendente</option>
                    {funcionarios.map(f => (
                      <option key={f.id} value={f.id}>{f.nome}</option>
                    ))}
                  </Form.Select>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Data de Retirada *</Form.Label>
                <DatePicker
                  selected={formData.dataRetirada ? new Date(formData.dataRetirada) : null}
                  onChange={(date) => handleDateChange('dataRetirada', date)}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  minDate={new Date()}
                  placeholderText="Selecione a data de retirada"
                  locale="pt-BR"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Label>Data de Devolução *</Form.Label>
              <DatePicker
                selected={formData.dataDevolucao ? new Date(formData.dataDevolucao) : null}
                onChange={(date) => handleDateChange('dataDevolucao', date)}
                dateFormat="dd/MM/yyyy"
                className="form-control"
                minDate={new Date()}
                placeholderText="Selecione a data de retirada"
                locale="pt-BR"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Status *</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <option value="reservado">Reservado</option>
                  {/* <option value="retirado">Retirado</option>
                  <option value="devolvido">Devolvido</option> */}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Observações</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="observacoes"
              value={formData.observacoes}
              onChange={handleChange}
              placeholder="Observações sobre a reserva"
            />
          </Form.Group>
          <Row>
            <Col>
              <FormPagamentos
                valorTotal={formData.produtos.reduce((sum, p) => sum + (p.precoTotal || 0), 0)}
                onChange={setPagamentosData}
                initialData={pagamentosData}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button variant="success" type="submit" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Form>
      <ConfirmModal
        show={cancelModalState.show}
        onHide={hideCancelConfirm}
        onConfirm={handleCancelConfirm}
        title={cancelModalState.title}
        message={cancelModalState.message}
        type={cancelModalState.type}
        confirmText={cancelModalState.confirmText}
        cancelText={cancelModalState.cancelText}
        loading={cancelModalState.loading}
      />
      <ReservaConfirmModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        onConfirm={confirmSave}
        cliente={selectedCliente}
        produtos={formData.produtos}
        dataRetiradaISO={formData.dataRetirada}
        dataDevolucaoISO={formData.dataDevolucao}
        status={formData.status}
        observacoes={formData.observacoes}
        pagamentosData={pagamentosData}
      />
    </Modal >
  );
};

export default ReservaForm;
